import React from "react";
import cn from "classnames";
import { TickCircle } from "components/icons/components/TickCircle";
import { CurrentStepIcon } from "static/media/current-step";
import { BUILDER_STEPS } from "../state/stepState";
import type { BuilderStep, StepCompletion } from "../config";

const STEP_LABELS: Record<BuilderStep, string> = {
  targeting: "Targeting",
  design: "Design",
  tracking: "Tracking",
};

const FutureCircle = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10.5" stroke="#cbd5e1" strokeWidth="1.5" className="dark:stroke-slate-600" />
  </svg>
);

export interface StepHeaderProps {
  activeStep: BuilderStep;
  completion: StepCompletion;
  reachable: Record<BuilderStep, boolean>;
  onStepSelect: (step: BuilderStep) => void;
}

const StepHeader = ({ activeStep, completion, reachable, onStepSelect }: StepHeaderProps) => (
  <nav className="flex items-center justify-center gap-4 py-4">
    {BUILDER_STEPS.map((step, index) => {
      const isActive = step === activeStep;
      const isComplete = completion[step] && reachable[step];
      const isSelectable = reachable[step] && !isActive;
      return (
        <React.Fragment key={step}>
          {index > 0 && <span className="h-px w-12 bg-slate-200 dark:bg-accent" />}
          <button
            type="button"
            // Same anchor the EDDM topbar carries, on the step key rather than
            // the label. Without it the redesigned shell's tabs were invisible
            // to Copilot control mode: the agent could reach the Design step
            // from the segments page (which is anchored) and then had no way
            // to leave it, because this header renders a different component
            // that #2490 never touched.
            data-cy={`campaignStep-${step}`}
            disabled={!reachable[step]}
            aria-current={isActive ? "step" : undefined}
            onClick={() => isSelectable && onStepSelect(step)}
            className={cn(
              "flex items-center gap-2 text-sm",
              isActive ? "font-semibold text-foreground" : reachable[step] ? "text-foreground" : "text-slate-400",
              isSelectable ? "cursor-pointer" : "cursor-default"
            )}
          >
            {isActive ? (
              <CurrentStepIcon width={22} height={22} />
            ) : isComplete ? (
              <TickCircle
                fill="#2E3860"
                size={22}
                className="dark:[&_path]:fill-slate-300 dark:[&_circle]:fill-slate-900"
              />
            ) : (
              <FutureCircle />
            )}
            {STEP_LABELS[step]}
          </button>
        </React.Fragment>
      );
    })}
  </nav>
);

export { StepHeader };
